import { useMutation } from '@tanstack/react-query';
import { useBrokerNewReferralStore } from '../state/useBrokerNewReferralStore';
import { BrokerClientApi } from '@apply/api';
import { applicationInsights } from '../../../../../ApplicationInsights';

export function useBrokerNewReferralActions() {
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);

    const referral = useBrokerNewReferralStore((s) => s.referral);
    const reset = useBrokerNewReferralStore((s) => s.reset);

    const mapToValidateDto = () => ({
        entityName: referral.businessEntityName,
        entityState: referral.stateIncorporated,
        principalFirstName: referral.repFirstName,
        principalLastName: referral.repLastName,
        principalEmail: referral.repEmail,
    });

    const mapToSaveDto = () => ({
        principalFirstName: referral.repFirstName ?? '',
        principalMiddleName: referral.repMiddleName ?? '',
        principalLastName: referral.repLastName ?? '',
        principalEmail: referral.repEmail ?? '',
        principalSuffix: referral.repSuffix ?? '',
        entityName: referral.businessEntityName ?? '',
        entityState: referral.stateIncorporated ?? '',
        acceptedIcca: referral.iccaAgreement,
        fontId: referral.fontId ?? null,
        clientIsFlagged: referral.clientIsFlagged,
        subjectPropertyInState: referral.subjectPropertyInAnyStates,
        subjectPropertyStateNMLSNumber: referral.stateNMLSNumber,
    });

    const validateMutation = useMutation({
        mutationFn: async () => {
            const dto = mapToValidateDto();
            return await brokerClientApi.validateReferral(dto);
        },
    });

    const saveMutation = useMutation({
        mutationFn: async () => {
            const dto = mapToSaveDto();
            return await brokerClientApi.addReferral(dto);
        },
    });

    const getBrokerLicenseNumberMutation = useMutation({
        mutationFn: async (brokerId: number) => {
            return await brokerClientApi.getBrokerLicenseNumber(brokerId);
        },
    });

    return {
        validate: () => validateMutation.mutate(),
        validateAsync: async () => {
            try {
                const data = await validateMutation.mutateAsync();
                return { ok: true, data };
            } catch (err: any) {
                return { ok: false, error: err };
            }
        },

        save: () => saveMutation.mutate(),
        saveAsync: async () => {
            try {
                const data = await saveMutation.mutateAsync();
                return { ok: true, data };
            } catch (err: any) {
                return { ok: false, error: err };
            }
        },
        getBrokerLicenseNumberAsync: async (brokerId: number) => {
            try {
                const data = await getBrokerLicenseNumberMutation.mutateAsync(brokerId);
                return { ok: true, data };
            } catch (err: any) {
                return { ok: false, error: err };
            }
        },

        reset,
    };
}
